import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSetAtom, useAtom } from 'jotai';
import { Button, Form, Input, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import useKpi from '@hooks/useKPI';
import { clearCache } from '@src/api/cacheAdapter/config';
import { refreshTrends } from '@store/common/actions';
import FieldElement from './FieldElement';
import { CREATE_NEW_SPI_CONFIG } from '@src/utils/kpi';
import kpiStore from '@src/globalStore/kpiStore';
import { validateKPIConfigData } from '../utils';
import { selectedKpiThresholdsAtom, editModeAtom } from '../atoms';
import {
  StyledForm,
  StyledKPIWrapper,
  StyledBody,
  StyledFooter,
  StyledName,
  StyledTop,
  StyledGoBackButton,
  StyledCaption,
} from '../styled';

const EditPanel = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const setIsKpiDrawerOpen = kpiStore((state: any) => state.setIsKpiDrawerOpen);
  const currentKpi = kpiStore((state: any) => state.currentKpi);
  const kpiList = kpiStore((state: any) => state.kpiList);
  const kpiMetaConfigs = kpiStore((state: any) => state.kpiMetaConfigs);
  const setEditMode = useSetAtom(editModeAtom);
  const { saveKpiThresholdsMutation, updateKpiThresholdsMutation, applyKpiMutation } = useKpi();
  const [selectedKpiThresholds, setSelectedKpiThresholds] = useAtom(selectedKpiThresholdsAtom);

  const onCancelHandler = () => {
    onGoBackHandler();
    setIsKpiDrawerOpen(false);
    setSelectedKpiThresholds(CREATE_NEW_SPI_CONFIG);
  };

  const onGoBackHandler = () => {
    setEditMode(false);
    setSelectedKpiThresholds(CREATE_NEW_SPI_CONFIG);
  };

  const onKpiTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedKpiThresholds({ ...selectedKpiThresholds, kpiName: e.target.value });
  };

  const onUpdateAndApplyHandler = async () => {
    const validation = await validateKPIConfigData(selectedKpiThresholds, kpiList);
    if (validation.status) {
      setIsLoading(true);
      await updateKpiThresholdsMutation.mutate(selectedKpiThresholds, {
        onSuccess: async () => {
          if (selectedKpiThresholds.kpiID === currentKpi.kpiID) {
            message.info(
              `${selectedKpiThresholds.kpiName} KPI has been updated and applied Successfully.`,
            );
            setIsLoading(false);
            clearCache();
            dispatch(refreshTrends());
          } else {
            await applyKpiMutation.mutate(selectedKpiThresholds.kpiID, {
              onSuccess: () => {
                message.info(
                  `${selectedKpiThresholds.kpiName} KPI has been updated and applied Successfully.`,
                );
                setIsLoading(false);
                clearCache();
                dispatch(refreshTrends());
              },
              onError: () => {
                message.error('There was an error while applying KPI. Please try again later.');
                setIsLoading(false);
              },
            });
          }
          onGoBackHandler();
          setIsKpiDrawerOpen(false);
        },
        onError: () => {
          message.error('There was an error while updating KPI. Please try again later.');
          setIsLoading(false);
        },
      });
    } else {
      message.error(validation.errorMessage);
    }
  };

  const onCreateAndApplyHandler = async () => {
    const validation = await validateKPIConfigData(selectedKpiThresholds, kpiList);
    if (validation.status) {
      setIsLoading(true);
      saveKpiThresholdsMutation.mutate(selectedKpiThresholds, {
        onSuccess: () => {
          message.info(
            `${selectedKpiThresholds.kpiName} KPI has been created and applied Successfully.`,
          );
          setIsLoading(false);
          clearCache();
          dispatch(refreshTrends());
          onGoBackHandler();
          setIsKpiDrawerOpen(false);
        },
        onError: () => {
          message.error('There was an error while creating KPI. Please try again later.');
          setIsLoading(false);
        },
      });
    } else {
      message.error(validation.errorMessage);
    }
  };

  return (
    <>
      <StyledBody>
        <StyledKPIWrapper>
          <StyledTop>
            <p>Edit KPI</p>
            <StyledGoBackButton onClick={() => onGoBackHandler()}>
              <ArrowLeftOutlined /> Go Back
            </StyledGoBackButton>
          </StyledTop>
          <StyledName>
            <label>KPI Name</label>
            <Input
              value={selectedKpiThresholds?.kpiName ?? ''}
              onChange={(e) => onKpiTextChange(e)}
            />
          </StyledName>
        </StyledKPIWrapper>
        <StyledCaption>
          <p>A minute is considered good if</p>
        </StyledCaption>
        <StyledForm>
          <Form layout="horizontal">
            {Object.keys(kpiMetaConfigs).map((thresholdKey, index) => {
              return (
                <FieldElement
                  key={thresholdKey}
                  kpiIndex={index}
                  thresholdKey={thresholdKey}
                  thresholdValue={selectedKpiThresholds[thresholdKey]}
                />
              );
            })}
          </Form>
        </StyledForm>
      </StyledBody>
      <StyledFooter>
        <Button type="primary" onClick={onCancelHandler} ghost>
          Cancel
        </Button>
        {selectedKpiThresholds && selectedKpiThresholds.kpiID ? (
          <Button type="primary" onClick={onUpdateAndApplyHandler} loading={isLoading}>
            Update and Apply KPI
          </Button>
        ) : (
          <Button type="primary" onClick={onCreateAndApplyHandler} loading={isLoading}>
            Create and Apply KPI
          </Button>
        )}
      </StyledFooter>
    </>
  );
};

export default EditPanel;
